<template>
  <div v-outside-click="closeDropDown" class="dropdown__Wrapper" @click="toggleDropDown">
    <div class="kk-designer-view__btn" :title="title">
      <slot></slot>
    </div>

    <div v-if="isVisible && items" ref="dropdown" class="dropdown" :style="{ height: items.length > 5 ? '13rem' : '' }">
      <div
        v-for="(item, i) in items"
        :key="i"
        class="dropdown__item"
        :class="{ 'dropdown__item-active': i === index }"
        :title="item.title"
        :data-test-id="`dropdown-item-${i}`"
        @click="callFunction($event, item, i)"
      >
        <span
          class="dropdown__item-icon"
          :class="`${item.icon}`"
          :style="item.style"
          :data-test-id="'add-view-icon'"
        ></span>
        <div class="dropdown__item-text" :data-test-id="`item-text-${i}`">
          {{ item.title }}
        </div>
        <span v-if="item.title !== 'Add View'" class="dropdown__item--Actions">
          <span
            v-if="items.length - 1 > 1"
            :class="icons.Trash"
            title="Delete View (delete key)"
            :data-test-id="`delete-view-item-${i}`"
            @click="deleteView($event, item)"
          ></span>
          <span
            :class="icons.Edit"
            title="Edit View (f2)"
            :data-test-id="`edit-view-item-${i}`"
            @click="editView($event, item)"
          ></span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import hotkeys from "hotkeys-js";
import { HotkeyScopes } from "../../../utils/constants";
import { icons } from "../../../utils/icons";

export default {
  props: ["items", "title", "activeIndex"],
  emits: ["toggle-dropdown"],
  data() {
    return {
      index: this.activeIndex,
      previousScope: null,
      icons: icons,
      previousIndex: null,
    };
  },
  computed: {
    isVisible() {
      return this.$store.state.views.isViewVisible;
    },
    showDialog() {
      return this.$store.state.panel.deleteDialog.show;
    },
  },
  watch: {
    activeIndex(newValue, oldValue) {
      this.index = newValue;
    },
    isVisible(newValue, oldValue) {
      if (this.isVisible) {
        this.$nextTick(() => {
          this.showActions(this.index);
        });
        this.setScopes();
      } else {
        this.unsetScopes();
      }
    },
  },
  beforeUnmount() {
    hotkeys.unbind("up, down, enter, escape,delete,f2", HotkeyScopes.DropDown);
  },
  methods: {
    scrollToActiveView() {
      const dropdown = this.$refs.dropdown;
      if (dropdown) {
        const selectedItem = dropdown.querySelector(".dropdown__item-active");
        if (selectedItem) {
          selectedItem.scrollIntoView({
            behavior: "smooth",
            block: "center", // Scroll to bottom of visible area
          });
        }
      }
    },

    showActions(index) {
      const dropdown = this.$refs.dropdown;
      if (dropdown) {
        const items = dropdown.querySelectorAll(".dropdown__item--Actions");
        items.forEach((item, i) => {
          item.style.display = i === index ? "flex" : "none";
        });
      }
    },
    deleteView(event, item) {
      event.stopPropagation();
      this.$store.dispatch("panel/setDeleteDialog", {
        ids: item.id,
        show: true,
        title: this.$t("sidebar.delete.view.title"),
        description: this.$t("sidebar.delete.view.description"),
        itemType: "views",
        action: () => {
          this.$store.dispatch(`views/remove`, { ids: item.id });
        },
      });
      this.closeDropDown();
    },
    editView(event, item) {
      event.stopPropagation();
      this.closeDropDown();
      this.$store.dispatch("views/setShowAddViewDialog", true);
      this.$store.dispatch("views/setEditingViewID", item.id);
    },
    handleKey(action) {
      return (event) => {
        if (this.isVisible && this.items.length) {
          const selectedItem = this.items[this.index];
          if (selectedItem.title !== "Add View") {
            if (action === "deleteView") {
              if (this.items.length > 2) {
                this.deleteView(event, selectedItem);
              }
            } else if (action === "editView") {
              this.editView(event, selectedItem);
            }
          }
        }
      };
    },
    toggleDropDown() {
      this.$store.dispatch("views/toggleViewsVisibility");
      if (!this.isVisible) {
        this.$nextTick(() => {
          this.scrollToActiveView();
        });
      }
    },
    closeDropDown() {
      this.$store.dispatch("views/setViewsVisibility", false);
      this.index = this.activeIndex;
    },
    callFunction(event, item, index) {
      if (item.title !== "Add View") {
        this.index = index;
      }
      item.func && item.func(item);
    },
    enter(event, handler) {
      event.preventDefault();
      const item = this.items[this.index];
      this.callFunction(event, item, this.index);
      this.closeDropDown();
    },
    scrollToIndex() {
      const dropdown = this.$refs.dropdown;
      if (dropdown) {
        const item = dropdown.querySelector(".dropdown__item");
        if (item) {
          const itemHeight = item.offsetHeight;
          const dropdownHeight = dropdown.clientHeight;
          const scrollPosition = dropdown.scrollTop;
          const indexPosition = this.index * itemHeight;

          // Only scroll if the selected item is out of the visible range
          if (indexPosition <= scrollPosition + dropdownHeight) {
            dropdown.scrollTop = indexPosition;
          } else if (indexPosition >= scrollPosition + dropdownHeight) {
            dropdown.scrollTop = indexPosition - dropdownHeight + itemHeight;
          }
        }
      }
    },
    moveDown(event) {
      event.preventDefault();
      this.index = (this.index + 1) % this.items.length;
      this.scrollToIndex();
      this.showActions(this.index); // Show actions for the current item
    },
    moveUp(event) {
      event.preventDefault();
      this.index = this.index <= 0 ? this.items.length - 1 : (this.index - 1) % this.items.length;
      this.scrollToIndex();
      this.showActions(this.index); // Show actions for the current item
    },
    setScopes() {
      this.previousScope = hotkeys.getScope();
      this.$store.dispatch("setScope", HotkeyScopes.DropDown);
      hotkeys.unbind("up, down, enter, escape,delete,f2", HotkeyScopes.DropDown);
      hotkeys("delete", HotkeyScopes.DropDown, this.handleKey("deleteView"));
      hotkeys("f2", HotkeyScopes.DropDown, this.handleKey("editView"));
      hotkeys("up", HotkeyScopes.DropDown, this.moveUp);
      hotkeys("down", HotkeyScopes.DropDown, this.moveDown);
      hotkeys("Enter", HotkeyScopes.DropDown, this.enter);
      hotkeys("escape", HotkeyScopes.DropDown, () => {
        this.closeDropDown();
        this.setPreviousScope();
      });
    },
    unsetScopes() {
      if (!this.showDialog) this.$store.dispatch("setScope", HotkeyScopes.ProcessDesigner);
      hotkeys.unbind("up, down, enter, escape,delete,f2", HotkeyScopes.DropDown);
    },
    setPreviousScope() {
      this.$store.dispatch("setScope", HotkeyScopes.ProcessDesigner);
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables";

::-webkit-scrollbar {
  width: 4px;
  height: 10px;
}

.kk-designer-view {
  &__btn {
    display: flex;
    gap: 0.2rem;
    align-items: center;
    cursor: pointer;
    margin: 0 0.4rem;
  }
}

.dropdown {
  &__wrapper {
    position: relative;
  }

  width: 14rem;
  overflow-y: auto;
  overflow-x: hidden;
  background: @kk-color-white;
  position: absolute;
  transform: translate(-40%, 0%);
  top: 100%;
  z-index: 200;
  box-shadow: @kk-primary-box-shadow;
  border-radius: 4px;

  &__item {
    font-family: "FiraSans-Regular", "Fira Sans", sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    color: @kk-color-gray;
    text-rendering: optimizeLegibility;
    position: relative;
    display: flex;
    gap: 0.4rem;
    align-items: center;
    padding: 0.5rem 0.4rem;
    height: 40px;
    cursor: pointer;

    &--Actions {
      display: none;
      justify-content: space-around;
      align-items: center;
      width: 50px;
    }

    img {
      width: 2rem;
    }

    &-icon {
      font-size: 18px;
      margin: 0 5px;
    }
    &-text {
      flex-grow: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: calc(100% - 80px); /* Adjust width considering the actions span */
    }
    &:not(&-active)&:hover {
      background-color: @kk-color-light-blue;
    }

    &:hover > &--Actions {
      display: flex !important;
    }

    &-active {
      background-color: @kk-color-primary-blue;
      border-bottom: 2px solid @kk-color-primary-blue;
      color: @kk-color-white;
    }
  }
}
</style>
